import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { useInstallationContext } from '../../../../../context/installation';
import {
  DeviceDetailsQuery,
  DeviceDetailsQueryVariables,
  NotificationCode,
  UserDevice,
} from '../../../../../data-access/gql-types/graphql';
import { DEVICE_DETAILS } from '../../../../../data-access/queries/devices';
import { refetchDataOnNotify } from '../../../../../hooks/notifications/use-notify';
import { ROUTES } from '../../../../../routes/routes';
import { useDevicesAndChannelsContext } from '../../../context/devices-and-channels';

export const useGetDeviceDetails = (deviceId: string) => {
  const navigate = useNavigate();
  const { selectedInstallationId, skipLavvaFetch } = useInstallationContext();
  const { setDeviceDetails, clearDeviceDetails } = useDevicesAndChannelsContext();
  const { data, loading, refetch } = useQuery<DeviceDetailsQuery, DeviceDetailsQueryVariables>(DEVICE_DETAILS, {
    variables: { installationId: selectedInstallationId, deviceId: deviceId },
    fetchPolicy: 'no-cache',
    skip: skipLavvaFetch,
    onCompleted: (data) => {
      if (!data.userDevice) navigate(ROUTES.Root(), { replace: true });
    },
  });

  useEffect(() => {
    if (data?.userDevice) setDeviceDetails(data.userDevice as UserDevice);
    return () => clearDeviceDetails();
  }, [data]);

  const refetchDeviceDetails = async () => {
    try {
      const result = await refetch();
      if (result.data.userDevice) setDeviceDetails(result.data.userDevice as UserDevice);

      return result;
    } catch (error) {
      console.error('Error refetching device:', error);
      throw error;
    }
  };

  refetchDataOnNotify(NotificationCode.ResetToDefaults, () => {
    refetchDeviceDetails();
  });

  return {
    data,
    loading,
    refetch,
  };
};
